'use client';

import { useState } from 'react';
import { ScoredTechnology } from '@/lib/tech-stack-recommender';

interface ComparisonProps {
  technologies: ScoredTechnology[];
}

type DimensionKey = 'overview' | 'cost' | 'technical' | 'operations' | 'ecosystem' | 'compliance' | 'suitability' | 'migration';

const DIMENSIONS: { key: DimensionKey; label: string; description: string }[] = [
  { key: 'overview', label: 'Overview', description: 'Basic information and description' },
  { key: 'cost', label: 'Cost', description: 'Pricing model and licensing' },
  { key: 'technical', label: 'Technical', description: 'Performance and scalability' },
  { key: 'operations', label: 'Operations', description: 'Hosting and maintenance' },
  { key: 'ecosystem', label: 'Ecosystem', description: 'Community and support' },
  { key: 'compliance', label: 'Compliance', description: 'Security and regulatory compliance' },
  { key: 'suitability', label: 'Suitability', description: 'Use cases and fit' },
  { key: 'migration', label: 'Migration', description: 'Vendor lock-in and portability' },
];

export default function TechStackComparison({ technologies }: ComparisonProps) {
  const [selectedDimensions, setSelectedDimensions] = useState<DimensionKey[]>([
    'overview',
    'cost',
    'technical',
    'compliance',
  ]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['overview']));
  const [highlightDifferences, setHighlightDifferences] = useState(false);

  const toggleDimension = (dimension: DimensionKey) => {
    if (selectedDimensions.includes(dimension)) {
      setSelectedDimensions(selectedDimensions.filter(d => d !== dimension));
    } else {
      setSelectedDimensions([...selectedDimensions, dimension]);
    }
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const expandAll = () => {
    setExpandedSections(new Set(selectedDimensions));
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  const exportToCSV = () => {
    const headers = ['Dimension', 'Field', ...technologies.map(t => t.name)];
    const rows: string[][] = [headers];

    selectedDimensions.forEach(dimension => {
      const data = getComparisonData(dimension, technologies);
      data.forEach(row => {
        rows.push([dimension, row.label, ...row.values]);
      });
    });

    const csv = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tech-stack-comparison.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Controls */}
      <div className="mb-6 space-y-4">
        {/* Dimension filters */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-2">Show Dimensions:</h3>
          <div className="flex flex-wrap gap-2">
            {DIMENSIONS.map(dimension => (
              <button
                key={dimension.key}
                onClick={() => toggleDimension(dimension.key)}
                className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                  selectedDimensions.includes(dimension.key)
                    ? 'bg-accent text-accent-foreground font-semibold'
                    : 'bg-surface text-foreground-secondary hover:bg-surface/70'
                }`}
                title={dimension.description}
              >
                {dimension.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={expandAll}
            className="px-4 py-2 bg-surface text-foreground rounded-lg text-sm hover:bg-surface/70 transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-4 py-2 bg-surface text-foreground rounded-lg text-sm hover:bg-surface/70 transition-colors"
          >
            Collapse All
          </button>
          <button
            onClick={() => setHighlightDifferences(!highlightDifferences)}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              highlightDifferences
                ? 'bg-accent text-accent-foreground font-semibold'
                : 'bg-surface text-foreground hover:bg-surface/70'
            }`}
          >
            Highlight Differences
          </button>
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-semibold hover:bg-accent-hover transition-colors"
          >
            Export to CSV
          </button>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-surface rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground border-b border-border-muted">
                  Dimension
                </th>
                {technologies.map((tech, index) => (
                  <th
                    key={tech.id}
                    className="px-4 py-3 text-left text-sm font-semibold text-foreground border-b border-border-muted"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-accent">#{tech.rank}</span>
                        <span>{tech.name}</span>
                      </div>
                      <div className="text-xs font-normal text-foreground-tertiary">
                        Score: {tech.score.toFixed(1)}/100
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedDimensions.map(dimension => (
                <ComparisonSection
                  key={dimension}
                  dimension={dimension}
                  technologies={technologies}
                  isExpanded={expandedSections.has(dimension)}
                  onToggle={() => toggleSection(dimension)}
                  highlightDifferences={highlightDifferences}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ComparisonSection({
  dimension,
  technologies,
  isExpanded,
  onToggle,
  highlightDifferences,
}: {
  dimension: DimensionKey;
  technologies: ScoredTechnology[];
  isExpanded: boolean;
  onToggle: () => void;
  highlightDifferences: boolean;
}) {
  const dimensionLabel = DIMENSIONS.find(d => d.key === dimension)?.label || dimension;
  const data = getComparisonData(dimension, technologies);

  return (
    <>
      {/* Section header */}
      <tr className="bg-background/50 cursor-pointer hover:bg-background/70 transition-colors" onClick={onToggle}>
        <td colSpan={technologies.length + 1} className="px-4 py-3 border-b border-border-muted">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-accent">{dimensionLabel}</span>
            <svg
              className={`w-5 h-5 text-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </td>
      </tr>

      {/* Section content */}
      {isExpanded && data.map((row, index) => {
        const values = row.values;
        const rationales = row.rationales;
        const allSame = values.every(v => v === values[0]);
        const shouldHighlight = highlightDifferences && !allSame;

        return (
          <tr
            key={`${dimension}-${index}`}
            className={`border-b border-border-muted ${shouldHighlight ? 'bg-accent-muted' : ''}`}
          >
            <td className="px-4 py-3 text-sm text-foreground-secondary font-medium w-48">
              {row.label}
            </td>
            {values.map((value, techIndex) => (
              <td
                key={techIndex}
                className={`px-4 py-3 text-sm text-foreground ${
                  shouldHighlight && value !== values[0] ? 'font-semibold' : ''
                } ${rationales ? 'cursor-help' : ''}`}
                title={rationales ? rationales[techIndex] : undefined}
              >
                {formatValue(value)}
              </td>
            ))}
          </tr>
        );
      })}
    </>
  );
}

function getComparisonData(
  dimension: DimensionKey,
  technologies: ScoredTechnology[]
): { label: string; values: any[]; rationales?: string[] }[] {
  switch (dimension) {
    case 'overview':
      return [
        { label: 'Description', values: technologies.map(t => t.description) },
        { label: 'Website', values: technologies.map(t => t.website) },
        { label: 'Category', values: technologies.map(t => t.category) },
        { label: 'License', values: technologies.map(t => t.license) },
      ];

    case 'cost':
      return [
        { label: 'Pricing Model', values: technologies.map(t => t.pricing.model) },
        { label: 'Starting Price', values: technologies.map(t => t.pricing.startingPrice || 'Free') },
        { label: 'Free Option', values: technologies.map(t => t.pricing.freeOption ? 'Yes' : 'No') },
        { label: 'Cost Score', values: technologies.map(t => `${Math.round(t.scoreBreakdown.cost * 100)}/100`), rationales: technologies.map(t => t.scoreRationales.cost) },
      ];

    case 'technical':
      return [
        { label: 'Languages', values: technologies.map(t => t.technical.language.join(', ')) },
        { label: 'Architecture', values: technologies.map(t => t.technical.architecture.join(', ')) },
        { label: 'Scalability', values: technologies.map(t => t.technical.scalability) },
        { label: 'Performance', values: technologies.map(t => t.technical.performance) },
        { label: 'Learning Curve', values: technologies.map(t => t.technical.learningCurve) },
        { label: 'Maturity', values: technologies.map(t => t.technical.maturity) },
        { label: 'Technical Score', values: technologies.map(t => `${Math.round(t.scoreBreakdown.technical * 100)}/100`), rationales: technologies.map(t => t.scoreRationales.technical) },
      ];

    case 'operations':
      return [
        { label: 'Hosting Options', values: technologies.map(t => t.operations.hostingOptions.join(', ')) },
        { label: 'Maintenance', values: technologies.map(t => t.operations.maintenanceComplexity) },
        { label: 'Monitoring', values: technologies.map(t => t.operations.monitoringComplexity) },
        { label: 'Backup/Recovery', values: technologies.map(t => t.operations.backupRecovery) },
        { label: 'Operational Score', values: technologies.map(t => `${Math.round(t.scoreBreakdown.operational * 100)}/100`), rationales: technologies.map(t => t.scoreRationales.operational) },
      ];

    case 'ecosystem':
      return [
        { label: 'Community Size', values: technologies.map(t => t.ecosystem.communitySize) },
        { label: 'Documentation', values: technologies.map(t => t.ecosystem.documentation) },
        { label: 'Enterprise Support', values: technologies.map(t => t.ecosystem.enterpriseSupport ? 'Yes' : 'No') },
        { label: 'Popularity Trend', values: technologies.map(t => t.ecosystem.popularityTrend) },
        { label: 'Key Integrations', values: technologies.map(t => t.ecosystem.integrations.slice(0, 3).join(', ')) },
        { label: 'Ecosystem Score', values: technologies.map(t => `${Math.round(t.scoreBreakdown.ecosystem * 100)}/100`), rationales: technologies.map(t => t.scoreRationales.ecosystem) },
      ];

    case 'compliance':
      return [
        { label: 'GDPR', values: technologies.map(t => t.compliance.gdpr ? '✓' : '✗') },
        { label: 'HIPAA', values: technologies.map(t => t.compliance.hipaa ? '✓' : '✗') },
        { label: 'SOC 2', values: technologies.map(t => t.compliance.soc2 ? '✓' : '✗') },
        { label: 'PCI DSS', values: technologies.map(t => t.compliance.pci ? '✓' : '✗') },
        { label: 'Encryption', values: technologies.map(t => t.compliance.security.encryption ? '✓' : '✗') },
        { label: 'Audit Logs', values: technologies.map(t => t.compliance.security.auditLogs ? '✓' : '✗') },
        { label: 'RBAC', values: technologies.map(t => t.compliance.security.rbac ? '✓' : '✗') },
        { label: 'MFA', values: technologies.map(t => t.compliance.security.mfa ? '✓' : '✗') },
        { label: 'Compliance Score', values: technologies.map(t => `${Math.round(t.scoreBreakdown.compliance * 100)}/100`), rationales: technologies.map(t => t.scoreRationales.compliance) },
      ];

    case 'suitability':
      return [
        { label: 'Team Sizes', values: technologies.map(t => t.suitability.teamSize.join(', ')) },
        { label: 'Top Use Cases', values: technologies.map(t => t.suitability.useCases.slice(0, 3).join(', ')) },
        { label: 'Key Strengths', values: technologies.map(t => t.suitability.strengths.slice(0, 3).join('; ')) },
        { label: 'Key Weaknesses', values: technologies.map(t => t.suitability.weaknesses.slice(0, 3).join('; ')) },
        { label: 'Strategic Score', values: technologies.map(t => `${Math.round(t.scoreBreakdown.strategic * 100)}/100`), rationales: technologies.map(t => t.scoreRationales.strategic) },
      ];

    case 'migration':
      return [
        { label: 'Data Portability', values: technologies.map(t => t.migration.dataPortability) },
        { label: 'Vendor Lock-in', values: technologies.map(t => t.migration.vendorLockIn) },
        { label: 'Migration Paths', values: technologies.map(t => t.migration.migrationPath.slice(0, 3).join(', ')) },
      ];

    default:
      return [];
  }
}

function formatValue(value: any): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}
